import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OnboardingLayout from '../components/OnboardingLayout';
import VerifyEmailBanner from '../components/VerifyEmailBanner';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const OPTIONS = [
  { value: 'private_room_shared_house', label: 'A private room in a shared house', description: 'Usually the most affordable option' },
  { value: 'basement_apartment', label: 'A basement apartment', description: 'Common in the GTA, often with a separate entrance' },
  { value: 'studio', label: 'A studio', description: 'Self-contained, just for you' },
  { value: 'one_bedroom', label: 'A 1-bedroom apartment', description: 'More space, higher price' },
  { value: 'open_to_anything', label: 'Open to anything', description: "Show me everything that fits my budget" },
];

const OnboardingPlaceType = () => {
  const navigate = useNavigate();
  const { user, loading, updateOnboarding } = useAuth();
  const [selected, setSelected] = useState(new Set(user?.place_types_interested || []));
  const [saving, setSaving] = useState(false);

  if (loading) return null;
  if (!user) {
    navigate('/signup', { replace: true });
    return null;
  }

  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setSelected(next);
  };

  const saveAndContinue = async () => {
    setSaving(true);
    try {
      await updateOnboarding({
        place_types_interested: Array.from(selected),
      });
      navigate('/onboarding/living-situation');
    } catch (e) {
      toast.error("We couldn't save that. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <VerifyEmailBanner />
      <OnboardingLayout
        step={4}
        title="What type of place are you looking for?"
        subtitle="Pick all that apply."
        testId="onboarding-place-type"
      >
        <div className="space-y-2.5">
          {OPTIONS.map((opt) => {
            const active = selected.has(opt.value);
            return (
              <label
                key={opt.value}
                className={`flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                  active
                    ? 'border-amber-500 bg-amber-50/60 shadow-soft'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                }`}
                data-testid={`place-type-option-${opt.value}`}
              >
                <Checkbox
                  checked={active}
                  onCheckedChange={() => toggle(opt.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="font-medium text-slate-900 text-base">{opt.label}</p>
                  <p className="text-sm text-slate-600 mt-0.5">{opt.description}</p>
                </div>
              </label>
            );
          })}
        </div>

        <div className="mt-8 space-y-3">
          <Button
            onClick={saveAndContinue}
            disabled={saving}
            className="w-full h-12 btn-primary text-base"
            data-testid="place-type-continue-btn"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue
          </Button>
          <button
            onClick={() => navigate('/onboarding/living-situation')}
            className="w-full text-sm text-slate-600 hover:text-slate-900 py-2"
            data-testid="place-type-skip-link"
          >
            Skip this step
          </button>
        </div>
      </OnboardingLayout>
    </>
  );
};

export default OnboardingPlaceType;
